<script setup lang="ts">

import {computed, inject, ref, watch} from "vue";
import {useStore} from "vuex";
import {ParametersVariations} from "@/assets/ts/parametersVariations";

const props = withDefaults(defineProps<{
  groupIdx: number;
  prioSettings?: (string | number)[];
}>(), {
  prioSettings: () => []
});
const emit = defineEmits(["patch"]);

const store = useStore();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

/**
 * session
 *
 * Transferred from itemV3StonesSetting.vue: configureAll/itemIdx are wired into
 * ParametersVariations the same way, the variant is fetched per stone group via
 * getItemVariants(itemIdx, "stones.<groupIdx>.stone_form_angle") instead of a fixed idx.
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const variantIdx = ""

const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

/**
 * stone form angle - stones.<groupIdx>.stone_form_angle
 */

const stoneFormAngle = ref({
  "stone_form_angle": {}
} as any)

const getStoneFormAngleData = (groupIdy: number) => {
  if (groupIdy >= 0) {
    stoneFormAngle.value.stone_form_angle = parametersVariations.getItemVariants(itemIdx.value, "stones." + groupIdy + ".stone_form_angle")
  }
}

watch(() => props.groupIdx, (groupIdy) => {
  getStoneFormAngleData(groupIdy)
}, {immediate: true})

watch(session, () => {
  getStoneFormAngleData(props.groupIdx)
}, {deep: true})

/**
 * form angle variants
 */

const active = computed(() => stoneFormAngle.value.stone_form_angle[0])

/*
 * focus values (transferred from itemV3StonesSetting.vue)
 * - empty list: show all values
 * - otherwise: only show values whose idx is part of the list
 */

const focusValueIdx = computed(() => (props.prioSettings || []).map((idx) => parseInt(idx.toString())))

const variantsList = computed(() => {
  const values = active.value?.values || []
  if (focusValueIdx.value.length === 0) return values
  return values.filter((v: { idx: number }) => focusValueIdx.value.indexOf(v.idx) !== -1)
})

// minimum amount of values the variant list needs before
// the component is shown - until then the whole component stays invisible (v-if = false)
const minVariantsToShow = 2

const activeIndex = computed(() => variantsList.value.findIndex((v: any) => v.match))

const variantsLoaded = computed(() => !!active.value && variantsList.value.length >= minVariantsToShow)

const sliderStyle = computed(() => {
  const count = variantsList.value.length;
  const activeIdx = activeIndex.value;
  if (count === 0 || activeIdx < 0 || activeIdx >= count) return {display: 'none'};

  const width = 100 / count;
  const left = activeIdx * width;

  return {
    width: `calc(${width}% - 4px)`,
    left: `calc(${left}% + 2px)`,
  };
});

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  emit('patch', {
    action: actionIdx,
    value: value.idx,
    certificated_diamond: value.certificated_diamond
  });
};

</script>

<template>
  <div v-if="variantsLoaded" class="w-full mb-2">
    <div
        class="relative flex items-center w-full h-[22px] max-h-[22px] bg-[#f3f0e9] rounded-full p-[2px] border border-[#e8e2d8] select-none">
      <!-- Sliding Background -->
      <div
          class="absolute top-[2px] bottom-[2px] bg-brand-main rounded-full transition-all duration-300 ease-in-out shadow-xs pointer-events-none"
          :style="sliderStyle"
      ></div>

      <!-- Angle Buttons -->
      <button
          v-for="(variantData, variantIdx) in variantsList"
          :key="variantIdx"
          type="button"
          class="text-[10px] tracking-wide relative flex-1 flex cursor-pointer items-center justify-center text-xs transition-colors duration-200 z-10 font-normal leading-none h-full"
          :class="variantData.match ? 'text-white font-medium' : 'text-gray-500 hover:text-gray-700'"
          @click="handleValuePatch(active.idx, variantData)"
      >
        {{ variantData.title }}°
      </button>
    </div>
  </div>
</template>

<style scoped>
</style>
